/**
 * @file weirwood 稳定性监控
 */

import type {Client} from '@baidu/weirwood-sdk';
import WeirwoodConfigParams from './weirwood.json';

const weirwoodOptions = {
    common: {
        // 只支持如下字符：英文字母(大小写)，数字(0-9)，下划线(_)，中划线(-)，点(.)，@符号
        buildid: WeirwoodConfigParams.buildid,
        token: WeirwoodConfigParams.token,
        ignoreUrls: [
            // 本地开发屏蔽错误、性能发送
            'localhost',
            '127.0.0.1',
        ],
    },
    error: {
        collectWindowErrors: true,
        // promise异常
        collectUnhandledRejections: true,
        // 静态资源加载异常
        collectResourceLoadErrors: false,
    },
    perf: {
        sampleRate: 1,
        SPA: true,
        History: true,
        afterOnload: true,
        interactionSampleRate: 0.1,
    },
};

type ErrorContext = Record<string, unknown>;
interface PendingError {
    errorInfo: unknown;
    context?: ErrorContext;
}

type PerfMetrics = Record<string, number | number[]>;

class WeirwoodLogger {
    static readonly PENDING_QUEUE_MAX_LENGTH = 100;
    private readonly exceptQueue: PendingError[] = [];
    private readonly perfQueue: PerfMetrics[] = [];
    private instance: Client | null = null;
    private initPromise: Promise<void> | null = null;

    /**
     * 初始化 Weirwood SDK。
     * 用动态 import 避免监控代码进入首屏关键执行链路。
     */
    init(): Promise<void> {
        // 避免重复初始化
        if (this.instance) {
            return Promise.resolve();
        }

        if (this.initPromise) {
            return this.initPromise;
        }

        // 动态加载 Weirwood，避免监控 SDK 进入首屏关键执行链路。
        this.initPromise = import('@baidu/weirwood-sdk')
            .then((Weirwood) => {
                if (this.instance) {
                    return;
                }

                this.instance = Weirwood.init(weirwoodOptions) as Client;
                this.uninstallEarlyErrorCapture();

                // 初始化完成后再补报早期缓存，确保延迟加载不丢手动上报和 early 捕获。
                this.clearPendingExceptAndPerf();
            })
            .catch(() => {
                this.initPromise = null;
            });

        return this.initPromise;
    }

    /**
     * 上报异常信息。
     * 初始化完成前先进入有界队列，避免延迟加载期间丢失手动上报和 early 捕获。
     */
    except = (errorInfo: unknown, context?: ErrorContext): void => {
        // 如果在发送之前，还没有被初始化，进行错误数据缓存
        if (!this.instance) {
            if (this.exceptQueue.length < WeirwoodLogger.PENDING_QUEUE_MAX_LENGTH) {
                this.exceptQueue.push({errorInfo, context});
            }

            return;
        }

        try {
            const payload = context === undefined ? undefined : {env: context};
            this.instance.error.captureException(errorInfo, payload);
        } catch {
            // 监控失败不阻塞业务。
        }
    };

    /**
     * 上报自定义性能数据。
     * Weirwood 未初始化时先缓存，初始化后统一补报。
     */
    perf = (metrics: PerfMetrics): void => {
        if (!this.instance) {
            if (this.perfQueue.length < WeirwoodLogger.PENDING_QUEUE_MAX_LENGTH) {
                this.perfQueue.push(metrics);
            }
            return;
        }

        try {
            this.instance.perf.addUserTiming(metrics);
        } catch {
            // 监控失败不阻塞业务。
        }
    };

    /**
     * 安装早期全局错误捕获。
     * 只做轻量捕获和入队，不触发 Weirwood SDK 加载。
     */
    installEarlyErrorCapture = (): void => {
        if (typeof window === 'undefined') {
            return;
        }

        // 早期只做轻量捕获和入队，不在首屏阶段加载 Weirwood SDK。
        window.addEventListener('error', this.handleEarlyError, true);
        window.addEventListener('unhandledrejection', this.handleEarlyRejection);
    };

    /**
     * 补报初始化前缓存的异常和性能数据，并在补报后清空队列。
     */
    private clearPendingExceptAndPerf(): void {
        this.exceptQueue.forEach((exceptItem) => {
            this.except(exceptItem.errorInfo, exceptItem.context);
        });

        this.perfQueue.forEach((perfMetrics) => {
            this.perf(perfMetrics);
        });

        this.exceptQueue.length = 0;
        this.perfQueue.length = 0;
    }

    /**
     * 卸载早期错误捕获。
     * Weirwood 初始化后会接管全局异常监听，卸载可避免重复上报。
     */
    private uninstallEarlyErrorCapture(): void {
        if (typeof window === 'undefined') {
            return;
        }

        window.removeEventListener('error', this.handleEarlyError, true);
        window.removeEventListener('unhandledrejection', this.handleEarlyRejection);
    }

    /**
     * 处理早期 window error。
     * 只处理 JS runtime error，资源加载错误保持和 collectResourceLoadErrors 配置一致。
     */
    private readonly handleEarlyError = (event: Event): void => {
        if (!(event instanceof ErrorEvent)) {
            return;
        }

        this.except(event.error ?? new Error(event.message || 'window error'), {
            source: 'early-window-error',
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
        });
    };

    /**
     * 处理早期未捕获的 Promise rejection。
     * reason 可能不是 Error，因此透传 unknown 给 Weirwood。
     */
    private readonly handleEarlyRejection = (event: PromiseRejectionEvent): void => {
        this.except(event.reason, {
            source: 'early-unhandledrejection',
        });
    };
}

const logger = new WeirwoodLogger();

export const captureError = logger.except;
export const reportPerf = logger.perf;

/**
 * 判断当前页面是否已经产生 FCP。
 */
function hasFcp(): boolean {
    return performance
        .getEntriesByType('paint')
        .some((entry) => entry.name === 'first-contentful-paint');
}

/**
 * 在 load 之后执行回调。
 * 作为不支持 paint observer 时的保守兜底。
 */
function runAfterLoad(callback: () => void): void {
    if (document.readyState === 'complete') {
        callback();
        return;
    }

    window.addEventListener('load', callback, {once: true});
}

/**
 * 在 FCP 之后执行回调。
 * 如果浏览器无法观测 paint entry，则退到 load 后执行。
 */
function runAfterFcp(callback: () => void): void {
    if (hasFcp()) {
        callback();
        return;
    }

    if (
        typeof PerformanceObserver === 'function' &&
        PerformanceObserver.supportedEntryTypes.includes('paint')
    ) {
        const observer = new PerformanceObserver((list) => {
            if (!list.getEntries().some((entry) => entry.name === 'first-contentful-paint')) {
                return;
            }

            observer.disconnect();
            callback();
        });

        try {
            observer.observe({type: 'paint', buffered: true});
            return;
        } catch {
            observer.disconnect();
        }
    }

    // 不支持 paint observer 时，用 load 作为“首屏关键流程已结束”的保守兜底。
    runAfterLoad(callback);
}

/**
 * 调度 Weirwood 初始化。
 * 策略是 FCP 后再等待空闲时机，尽量不抢占首屏渲染和主流程执行。
 */
export const scheduleWeirwoodInit = (): void => {
    if (typeof window === 'undefined') {
        void logger.init();
        return;
    }

    // 先等 FCP，再等主线程空闲，减少监控初始化对首屏渲染的抢占。
    runAfterFcp(() => {
        const init = () => {
            void logger.init();
        };

        if (typeof window.requestIdleCallback === 'function') {
            window.requestIdleCallback(init);
            return;
        }

        window.requestAnimationFrame(init);
    });
};

export const installEarlyErrorCapture = logger.installEarlyErrorCapture;
